import useToken from 'hooks/useToken';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Card, Col, Row, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import InfiniteScroll from 'react-infinite-scroll-component';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import { FiShoppingCart, FiCalendar } from 'react-icons/fi';
import { useToast } from 'context/providers/ToastProvider';

const OrdenesCliente = ({
    idCliente,
    ordenes,
    setOrdenes
}) => {

    const { token, setToken } = useToken();
    const { showErrorToast } = useToast();
    const [paginaOrdenes, setPaginaOrdenes] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [cargando, setCargando] = useState(true);
    const [totalOrdenes, setTotalOrdenes] = useState(0);

    /*
         Carga órdenes por primera vez 
         **/
    useEffect(() => {
        if (idCliente) {
            // Resetear estados cuando cambia el cliente
            setOrdenes([]);
            setPaginaOrdenes(1);
            setHasMore(true);
            setCargando(true);

            fetchMoreOrdenes();
        }
    }, [idCliente]);
    /*
     Obtiene Ordenes
     **/
    const fetchMoreOrdenes = async () => {
        try {
            const response = await fetch('/api/cliente/ObtenerOrdenes', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    idCliente: idCliente,
                    pagina: paginaOrdenes,
                    tamanioPagina: 20
                })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.iResponseType === 1) {
                    const parsed = JSON.parse(data.detalles);


                    setTotalOrdenes(parsed.table3[0].totalOrdenes);

                    const nuevasOrdenes = parsed.table2;
                    if (nuevasOrdenes && nuevasOrdenes.length > 0) {
                        if (paginaOrdenes === 1) {
                            setOrdenes(nuevasOrdenes);
                        } else {
                            setOrdenes(prev => [...prev, ...nuevasOrdenes]);
                        }
                        setPaginaOrdenes(prev => prev + 1);

                        if (nuevasOrdenes.length < 20) {
                            setHasMore(false);
                        }
                    } else {
                        setHasMore(false);
                    }
                } else {
                    showErrorToast(data.vMessage);
                    setHasMore(false);
                }
            } else {
                showErrorToast(response.status + ' ' + response.statusText);
                if (response.status === 401) {
                    setToken(null);
                    location.href = '';
                }
                setHasMore(false);
            }
        } catch (error) {
            showErrorToast("Error inesperado al obtener órdenes");
            setHasMore(false);
        } finally {
            setCargando(false);
        }
    };




    // Skeleton Loading para órdenes
    const OrdenSkeleton = () => (
        <Row>
            {[1, 2, 3, 4, 5, 6].map((i) => (
                <Col key={i} className="col-xxl-4 col-xl-6 col-lg-6 col-md-12 col-sm-12 col-12 mb-3">
                    <div style={{
                        padding: "12px",
                        border: "1px solid #e3e3e3",
                        borderRadius: "8px",
                        background: "transparent"
                    }}>
                        <Row>
                            <Col className="col-4">
                                <Skeleton height={20} width="80%" />
                            </Col>
                            <Col className="col-4">
                                <Skeleton height={18} width="90%" />
                            </Col>
                            <Col className="col-4">
                                <Skeleton height={16} width="70%" />
                            </Col>
                        </Row>
                    </div>
                </Col>
            ))}
        </Row>
    );
    return (
        <>
            {ordenes.length > 0 || cargando ? (
                <Card className="mb-2 shadow-sm border-0">
                    <Card.Header className="py-2 px-3 bg-transparent border-bottom">
                        <div className="d-flex align-items-center">
                            <FiShoppingCart className="text-primary me-2" size={14} />
                            <h5 className="mb-0 fw-semibold">Órdenes</h5>
                            {ordenes.length > 0 && (
                                <span className="badge bg-primary bg-opacity-10 text-primary ms-2 rounded-pill py-0 px-2 small">
                                    {totalOrdenes}
                                </span>
                            )}
                        </div>
                    </Card.Header>

                    <Card.Body className="p-2 overflow-x-hidden " id="scrollableTargetOrdenes" style={{ maxHeight: '400px' }}>
                        {cargando ? (
                            <OrdenSkeleton />
                        ) : (
                            <InfiniteScroll
                                dataLength={ordenes.length}
                                next={fetchMoreOrdenes}
                                hasMore={hasMore}
                                loader={
                                    <div className="d-flex justify-content-center align-items-center py-2">
                                        <Spinner animation="border" size="sm" variant="primary" className="me-2" />
                                        <small className="text-muted">Cargando...</small>
                                    </div>
                                }
                                scrollableTarget="scrollableTargetOrdenes"
                                    scrollThreshold={0.70}
                                    style={{ overflowX: 'hidden' }} // Agregar esto
                                endMessage={
                                    ordenes.length > 0 && (
                                        <div className="text-center py-2 border-top mt-2">
                                            <small className="text-muted">
                                                <FiShoppingCart size={10} className="me-1" />
                                                Sin más órdenes
                                            </small>
                                        </div>
                                    )
                                }
                            >
                                <Row className="g-1">
                                    {ordenes.map((orden, index) => (
                                        <Col key={index} xxl={4} xl={6} lg={6} md={12} sm={12}>
                                            <Card
                                                className={`h-100 ${!orden.bIsActive ? 'text-muted' : ''}`}
                                                bg={!orden.bIsActive ? 'light' : ''}
                                                border={!orden.bIsActive ? 'secondary' : 'light'}
                                                style={!orden.bIsActive ? { opacity: 0.7 } : {}}
                                            >
                                                <Card.Body className="p-2">
                                                    <div className="d-flex justify-content-between align-items-center"> 
                                                        {/* Lado izquierdo: Orden y Total */}
                                                        <div className="d-flex flex-column">
                                                            {/* Primera línea: Orden */}
                                                            <div className="d-flex align-items-center mb-1">
                                                                {!orden.bIsActive && (
                                                                    <OverlayTrigger
                                                                        placement="top"
                                                                        overlay={<Tooltip>Cancelada</Tooltip>}
                                                                    >
                                                                        <FontAwesomeIcon
                                                                            icon="ban"
                                                                            className="text-danger me-1"
                                                                            size="xs"
                                                                        />
                                                                    </OverlayTrigger>
                                                                )}
                                                                <Link
                                                                    to={"/ordenes/detalle/" + orden.idOrden}
                                                                    className="text-decoration-none"
                                                                >
                                                                    <div className="d-flex align-items-center">
                                                                        <img
                                                                            src="/img/ordenes96.png"
                                                                            height={14}
                                                                            className="me-1"
                                                                            alt=""
                                                                        />
                                                                        <small className="fw-semibold text-primary">
                                                                            #{orden.idOrden}
                                                                        </small>
                                                                    </div>
                                                                </Link>
                                                            </div>
                                                            <div className="d-flex align-items-center">
                                                                <small className="fw-bold">
                                                                    {(() => {
                                                                        const totalFormateado = orden.dTotal.toLocaleString('es-MX', {
                                                                            minimumFractionDigits: 0,
                                                                            maximumFractionDigits: 0
                                                                        });

                                                                        const shortName = orden.vCurrencyShortName || 'MXN';
                                                                        const symbol = orden.vCurrencySymbol || '$';

                                                                        return `${shortName} ${symbol}${totalFormateado}`;
                                                                    })()}
                                                                </small>
                                                            </div>


                                                        </div>

                                                        {/* Lado derecho: Fecha centrada verticalmente */}
                                                        <div className="d-flex align-items-center">
                                                            <FiCalendar className="text-muted me-1" size={12} />
                                                            <small className="text-muted fw-medium">
                                                                {new Date(orden.dFechaVenta).toLocaleDateString('es-ES', {
                                                                    day: '2-digit',
                                                                    month: 'short',
                                                                    year: 'numeric'
                                                                })}
                                                            </small>
                                                        </div>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    ))}
                                </Row>
                            </InfiniteScroll>
                        )}
                    </Card.Body>
                </Card>
            ) : null}
        </>
    );
};

OrdenesCliente.propTypes = {
    idCliente: PropTypes.string.isRequired,
    ordenes: PropTypes.array.isRequired,
    setOrdenes: PropTypes.func.isRequired
};

export default OrdenesCliente;
